import {
  Bell,
  ChevronDown,
  MessageSquare,
  Shield,
  User,
  Settings,
  Eye,
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useUserRole, UserRole } from "@/hooks/useUserRole";
import { CompactSearchBar } from "@/components/search/CompactSearchBar";
import { adminNavConfig } from "@/config/navigation/adminNav";
import { NavProfileMenu } from "@/components/navigation/NavProfileMenu";
import { getDisplayName, getFallbackInitial, getDisplayImage } from "@/lib/utils";
import { useAdminReviewNotifications } from "@/hooks/useAdminReviewNotifications";
import { useUserProfile } from "@/hooks/useUserProfile";
import { logger } from "@/lib/logger";
// CREDIT SYSTEM - Disabled for MVP, keep for post-production
// import { UnifiedCreditDisplay } from '@/components/credits/UnifiedCreditDisplay';

export function AdminTopNav() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { allRoles, role: currentRole, switchRole } = useUserRole();
  const { profile, isLoading: profileLoading } = useUserProfile();

  // Use "settings" section for system/settings items in profile menu
  const settingsSection = useMemo(
    () => adminNavConfig.sections.find((section) => section.id === "settings"),
    []
  );

  // Track last viewed timestamps
  const [lastViewedNotifications, setLastViewedNotifications] = useState<number>(
    parseInt(localStorage.getItem('admin-notifications-viewed') || '0')
  );
  const [lastViewedTickets, setLastViewedTickets] = useState<number>(
    parseInt(localStorage.getItem('admin-tickets-viewed') || '0')
  );

  // Track acknowledged review items (stored in localStorage)
  const [acknowledgedReviewItems, setAcknowledgedReviewItems] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('admin-acknowledged-review-items');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });

  // Helper to check if badge should show
  const shouldShowBadge = (count: number | null | undefined, lastViewed: number) => {
    if (!count || count === 0) return false;
    const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
    return lastViewed < oneDayAgo;
  };

  // Fetch admin review notifications (vendor applications + verifications)
  const { counts: reviewCounts } = useAdminReviewNotifications();

  // Fetch unread notifications
  const { data: notificationCount } = useQuery({
    queryKey: ['admin-notifications'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return 0;

      const { count, error } = await supabase
        .from('notification_history')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .is('read_at', null);

      if (error) {
        logger.warn('Error fetching notifications count', { error });
        return 0;
      }

      return count || 0;
    },
    staleTime: 0, // Always refetch to get latest count
    refetchInterval: 10 * 1000, // Refetch every 10 seconds for real-time feel
  });

  // Set up real-time subscription for notification updates
  useEffect(() => {
    let mounted = true;
    let channel: ReturnType<typeof supabase.channel> | null = null;
    let reviewChannel: ReturnType<typeof supabase.channel> | null = null;

    const setupSubscriptions = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !mounted) return;

      try {
        channel = supabase
          .channel('admin-notifications-updates')
          .on(
            'postgres_changes',
            {
              event: 'UPDATE',
              schema: 'public',
              table: 'notification_history',
              filter: `user_id=eq.${user.id}`,
            },
            () => {
              // Invalidate query when notification is updated (marked as read)
              if (mounted) {
                queryClient.invalidateQueries({ queryKey: ['admin-notifications'] });
              }
            }
          )
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'notification_history',
              filter: `user_id=eq.${user.id}`,
            },
            () => {
              // Invalidate query when new notification is created
              if (mounted) {
                queryClient.invalidateQueries({ queryKey: ['admin-notifications'] });
              }
            }
          )
          .subscribe();

        // Also listen for updates to review items
        reviewChannel = supabase
          .channel('admin-review-updates')
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'vendor_applications',
            },
            () => {
              if (mounted) {
                queryClient.invalidateQueries({ queryKey: ['admin-review-notifications'] });
              }
            }
          )
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'supplier_verifications',
            },
            () => {
              if (mounted) {
                queryClient.invalidateQueries({ queryKey: ['admin-review-notifications'] });
              }
            }
          )
          .subscribe();
      } catch (error) {
        logger.error('Error setting up notification subscriptions', { error });
      }
    };

    setupSubscriptions();

    return () => {
      mounted = false;
      if (channel) supabase.removeChannel(channel).catch(() => { });
      if (reviewChannel) supabase.removeChannel(reviewChannel).catch(() => { });
    };
  }, [queryClient]);

  // Calculate effective review count (excluding acknowledged items)
  // Acknowledge review items when user clicks the bell if they haven't been reviewed yet
  const effectiveReviewCount = useMemo(() => {
    if (!reviewCounts) return 0;
    // For now, we'll show all review items, but allow them to be acknowledged
    // Review items should only be hidden if they've been actually reviewed (status changed)
    // We'll track when user has viewed them, but still show them until resolved
    return reviewCounts.total;
  }, [reviewCounts]);

  // Total notifications = review items + regular notifications
  // Only show badge if there are truly unread items
  const totalNotificationCount = effectiveReviewCount + (notificationCount || 0);

  // Show review count prominently if there are pending reviews
  const hasPendingReviews = effectiveReviewCount > 0;

  // Fetch pending support tickets
  const { data: ticketCount } = useQuery({
    queryKey: ['admin-tickets'],
    queryFn: async () => {
      const { count } = await supabase
        .from('support_tickets')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'open');

      return count || 0;
    }
  });

  // Profile data comes from centralized useUserProfile hook

  const handleNotificationClick = async () => {
    const now = Date.now();
    localStorage.setItem('admin-notifications-viewed', now.toString());
    setLastViewedNotifications(now);

    // Auto-mark all regular notifications as read when bell is clicked
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // Mark all notification_history as read
        await supabase
          .from('notification_history')
          .update({ read_at: new Date().toISOString() })
          .eq('user_id', user.id)
          .is('read_at', null);

        // Try to mark legacy notifications as read (table might not exist)
        try {
          await supabase
            // @ts-expect-error Legacy notifications table may not exist in type definitions
            .from('notifications')
            .update({ read: true })
            .eq('user_id', user.id)
            .eq('read', false);
        } catch (legacyError) {
          // Legacy table might not exist, ignore error
          logger.debug('Legacy notifications table not available', { error: legacyError });
        }

        // Invalidate queries to refresh counts
        queryClient.invalidateQueries({ queryKey: ['admin-notifications'] });
        queryClient.invalidateQueries({ queryKey: ['notification-history'] });
        queryClient.invalidateQueries({ queryKey: ['admin-review-notifications'] });
      }
    } catch (error) {
      logger.warn('Error marking notifications as read', { error });
    }

    navigate('/admin/notifications');
  };

  const handleTicketClick = () => {
    const now = Date.now();
    localStorage.setItem('admin-tickets-viewed', now.toString());
    setLastViewedTickets(now);
    navigate('/admin/support');
  };

  const handleRoleSwitch = (newRole: UserRole) => {
    // Switch role first
    switchRole(newRole);
    toast.success(`Switched to ${newRole} portal`);

    // Use a longer delay and force navigation to ensure ProtectedRoute sees the updated state
    // The delay allows React to update all state and re-render components
    setTimeout(() => {
      // Force navigation with replace to prevent back button issues
      if (newRole === 'admin') {
        window.location.href = '/admin/dashboard';
      } else if (newRole === 'supplier') {
        window.location.href = '/supplier/dashboard';
      } else if (newRole === 'buyer') {
        window.location.href = '/buyer/dashboard';
      }
    }, 100);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    toast.success("Logged out successfully");
    navigate("/auth");
  };

  const profileActions = [
    {
      label: "My Profile",
      icon: User,
      onSelect: () => navigate("/admin/profile"),
    },
  ];

  const roleActions =
    allRoles.length > 1
      ? allRoles
        .filter((role) => role !== currentRole)
        .map((role) => ({
          label:
            role === "admin"
              ? "Admin Portal"
              : role === "supplier"
                ? "Supplier Portal"
                : "Buyer Portal",
          icon: Shield,
          onSelect: () => handleRoleSwitch(role),
        }))
      : undefined;

  const profileSections = [
    ...(settingsSection
      ? [
        {
          label: settingsSection.label ?? "Settings & Account",
          items: settingsSection.items.slice(0, 3).map((item) => ({
            label: item.label,
            icon: item.icon,
            onSelect: () => navigate(item.href),
          })),
        },
      ]
      : []),
  ];

  return (
    <header className="sticky top-0 z-30 border-b border-border bg-white px-6">
      <div className="mx-auto max-w-7xl">
        <div className="flex h-16 items-center gap-2 overflow-hidden sm:gap-4">
          <div className="hidden shrink-0 items-center gap-2 sm:gap-4 md:flex">
            {/* Workspace dropdown removed - items accessible via sidebar */}
          </div>
          <div className="mx-8 flex min-w-0 max-w-xl flex-1 items-center">
            <CompactSearchBar
              placeholder="Search products, suppliers, RFQs..."
              className="w-full min-w-0"
            />
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-6 overflow-visible">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="relative size-10 shrink-0 overflow-visible text-muted-foreground transition-colors hover:text-muted-foreground"
                    onClick={handleNotificationClick}
                    aria-label="Open notifications"
                  >
                    <Bell className="size-5" />
                    {totalNotificationCount > 0 && (
                      <Badge className={`pointer-events-none absolute -right-1 -top-1 z-10 flex size-5 min-w-5 items-center justify-center p-0 text-xs font-semibold text-white ${hasPendingReviews ? 'animate-pulse bg-red-600' : 'bg-orange-600'
                        }`}>
                        {totalNotificationCount > 99 ? '99+' : totalNotificationCount}
                      </Badge>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {hasPendingReviews ? (
                    <div className="text-sm">
                      <div className="mb-1 font-semibold">{totalNotificationCount} notifications</div>
                      <div className="text-xs text-muted-foreground">
                        {effectiveReviewCount} items need review
                        {notificationCount > 0 && ` • ${notificationCount} unread messages`}
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm">
                      <div className="font-semibold">{totalNotificationCount} unread notifications</div>
                      <div className="text-xs text-muted-foreground">Click to view</div>
                    </div>
                  )}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="relative size-10 shrink-0 overflow-visible text-muted-foreground transition-colors hover:text-muted-foreground"
                    onClick={handleTicketClick}
                    aria-label="Open support tickets"
                  >
                    <MessageSquare className="size-5" />
                    {shouldShowBadge(ticketCount, lastViewedTickets) && (
                      <Badge className="absolute -right-1 -top-1 z-10 flex size-5 min-w-5 items-center justify-center bg-orange-600 p-0 text-xs font-semibold text-white">
                        {ticketCount}
                      </Badge>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="text-sm">
                    <div className="font-semibold">{ticketCount || 0} open support tickets</div>
                    <div className="text-xs text-muted-foreground">Click to view and respond</div>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <div className="shrink-0">
              {profileLoading ? (
                <div className="size-9 animate-pulse rounded-full bg-muted ring-2 ring-muted" />
              ) : (
                <NavProfileMenu
                  name={getDisplayName(profile, 'admin', 'navigation')}
                  email={profile?.email || undefined}
                  avatarUrl={getDisplayImage(profile, 'admin', 'navigation') || undefined}
                  fallbackInitial={getFallbackInitial(profile, 'admin', 'navigation')}
                  primaryActions={profileActions}
                  sections={profileSections}
                  roleActions={roleActions}
                  onSignOut={handleSignOut}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
